import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import EventCard from "@/components/feature/EventCard";
import { useSEO } from "@/hooks/useSEO";
import { useHashScroll } from "@/hooks/useHashScroll";
import {
  eventList,
  formatHeat,
  getCategoryName,
  getCategoryPath,
  getEventBySlug,
  getRelatedEvents,
  sortByHeat,
} from "@/utils/content";

const sections = [
  { id: "cause", label: "前因", icon: "ri-seedling-line" },
  { id: "process", label: "经过", icon: "ri-loader-4-line" },
  { id: "timeline", label: "完整时间线", icon: "ri-time-line" },
  { id: "result", label: "结果与现状", icon: "ri-flag-line" },
];

export default function EventDetail() {
  useHashScroll();
  const { slug = "" } = useParams();
  const event = getEventBySlug(slug);
  const related = event ? getRelatedEvents(event) : [];
  const sameCategory = event
    ? sortByHeat(
        eventList.filter(
          (item) => item.categorySlug === event.categorySlug && item.id !== event.id,
        ),
      ).slice(0, 3)
    : [];
  const moreEvents = sameCategory.length > 0 ? sameCategory : related;
  const moreTitle = sameCategory.length > 0 ? "同分类的其他事件" : "你可能也想看的事件";

  const jsonLd = useMemo(() => {
    if (!event) return undefined;
    return [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: event.title,
        description: event.summary,
        dateModified: event.updatedAt,
        inLanguage: "zh-CN",
        articleSection: getCategoryName(event.categorySlug),
        keywords: event.tags.join(","),
        image: event.cover,
        author: { "@type": "Organization", name: "热搜吃瓜" },
        publisher: { "@type": "Organization", name: "热搜吃瓜" },
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `https://hot-melon-watch.example.com/event/${event.slug}`,
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "首页",
            item: "https://hot-melon-watch.example.com/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: getCategoryName(event.categorySlug),
            item: `https://hot-melon-watch.example.com/category/${event.categorySlug}`,
          },
          { "@type": "ListItem", position: 3, name: event.title },
        ],
      },
    ];
  }, [event]);

  useSEO({
    title: event
      ? `${event.title} | 来龙去脉与时间线复盘 - 热搜吃瓜`
      : "事件不存在 - 热搜吃瓜",
    description: event
      ? `${event.summary.slice(0, 80)}…按时间顺序梳理前因、经过与结果，完整复盘该事件的来龙去脉。`
      : "没有找到该事件，可返回瓜田总览查看全部事件。",
    keywords: event
      ? `热搜吃瓜,${event.title},热搜吃瓜时间线,${event.tags.join(",")}`
      : "热搜吃瓜",
    path: event ? `/event/${event.slug}` : "/events",
    jsonLd,
  });

  if (!event) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <SiteHeader />
        <main className="mx-auto flex w-full max-w-content flex-1 flex-col items-center justify-center px-4 py-24 text-center md:px-6">
          <span className="flex h-14 w-14 items-center justify-center rounded-md bg-background-200 text-foreground-500">
            <i className="ri-emotion-sad-line text-2xl"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
            这个事件暂时没有收录
          </h1>
          <p className="mt-3 max-w-md text-sm text-foreground-600">
            可能是链接已更新，去瓜田总览看看其他正在复盘的事件吧。
          </p>
          <Link
            to="/events"
            className="mt-6 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回瓜田总览
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full flex-1">
        <section className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-10">
            <Breadcrumbs
              items={[
                { label: getCategoryName(event.categorySlug), to: getCategoryPath(event.categorySlug) },
                { label: event.title },
              ]}
            />
            <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[1.15fr_1fr] lg:gap-12">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    to={getCategoryPath(event.categorySlug)}
                    className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
                  >
                    {getCategoryName(event.categorySlug)}
                  </Link>
                  <span className="rounded-md border border-primary-200 bg-primary-100 px-2.5 py-1 text-xs font-medium text-primary-700">
                    {event.status}
                  </span>
                  <span className="flex items-center gap-1 rounded-md bg-background-200 px-2.5 py-1 text-xs font-medium text-foreground-700">
                    <i className="ri-fire-fill"></i>
                    热度 {formatHeat(event.heat)}
                  </span>
                </div>
                <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
                  {event.title}
                </h1>
                <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                  {event.summary}
                </p>
                <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-600 md:text-sm">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line text-base"></i>
                    最近更新 {event.updatedAt}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-node-tree text-base"></i>
                    共 {event.timeline.length} 个时间线节点
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-hashtag text-base"></i>
                    {event.tags.join(" / ")}
                  </span>
                </div>
              </div>

              <div className="h-56 w-full overflow-hidden rounded-lg border border-background-200 md:h-72">
                <img
                  src={event.cover}
                  alt={`${event.title}事件封面图`}
                  title={`${event.title} 事件梳理与时间线复盘`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        <div className="mx-auto grid max-w-content grid-cols-1 gap-8 px-4 py-10 md:px-6 md:py-14 lg:grid-cols-[1fr_300px] lg:gap-12">
          <article className="min-w-0">
            <section id="cause" className="scroll-mt-32">
              <h2 className="flex items-center gap-2.5 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-seedling-line text-lg"></i>
                </span>
                前因：事情是怎么起的
              </h2>
              <p className="mt-4 rounded-lg border border-background-200 bg-background-100 p-5 text-sm leading-relaxed text-foreground-700 md:text-base">
                {event.cause}
              </p>
            </section>

            <section id="process" className="mt-10 scroll-mt-32">
              <h2 className="flex items-center gap-2.5 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                  <i className="ri-loader-4-line text-lg"></i>
                </span>
                经过：几个关键节点
              </h2>
              <ol className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {event.development.map((item, index) => (
                  <li
                    key={item}
                    className="flex gap-3 rounded-lg border border-background-200 bg-background-50 p-4"
                  >
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-accent-500 font-heading text-sm font-bold text-foreground-950">
                      {index + 1}
                    </span>
                    <span className="text-sm leading-relaxed text-foreground-700">{item}</span>
                  </li>
                ))}
              </ol>
            </section>

            <section id="timeline" className="mt-10 scroll-mt-32">
              <h2 className="flex items-center gap-2.5 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-time-line text-lg"></i>
                </span>
                完整时间线
              </h2>
              <p className="mt-3 text-sm text-foreground-600">
                按公开信息的发布时间排序，从最早一条读到最后一条，就是整件事的完整顺序。
              </p>
              <ol className="relative mt-6 flex flex-col gap-6 border-l-2 border-background-200 pl-6 md:gap-7 md:pl-8">
                {event.timeline.map((node) => (
                  <li key={`${node.time}-${node.title}`} className="relative">
                    <span className="absolute -left-[31px] top-1.5 flex h-4 w-4 items-center justify-center rounded-full border-2 border-primary-500 bg-background-50 md:-left-[39px]">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary-500"></span>
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-md bg-primary-100 px-2.5 py-1 text-xs font-medium text-primary-700">
                      <i className="ri-calendar-line text-sm"></i>
                      {node.time}
                    </span>
                    <h3 className="mt-2.5 font-heading text-base font-semibold text-foreground-950 md:text-lg">
                      {node.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600 md:text-base">
                      {node.desc}
                    </p>
                  </li>
                ))}
              </ol>
            </section>

            <section id="result" className="mt-10 scroll-mt-32">
              <h2 className="flex items-center gap-2.5 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className="ri-flag-line text-lg"></i>
                </span>
                结果与现状
              </h2>
              <p className="mt-4 rounded-lg border border-secondary-200 bg-secondary-100 p-5 text-sm leading-relaxed text-secondary-900 md:text-base">
                {event.result}
              </p>
            </section>

            <div className="mt-10 flex flex-wrap items-center gap-3 rounded-lg border border-background-200 bg-background-100 p-5">
              <p className="flex-1 text-sm text-foreground-600">
                事件状态：<span className="font-medium text-foreground-900">{event.status}</span> ·
                最近更新 {event.updatedAt}
              </p>
              <Link
                to={getCategoryPath(event.categorySlug)}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700"
              >
                看更多{getCategoryName(event.categorySlug)}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </article>

          <aside className="lg:sticky lg:top-32 lg:h-fit">
            <nav
              aria-label="本页目录"
              className="rounded-lg border border-background-200 bg-background-50 p-4"
            >
              <h2 className="font-heading text-sm font-semibold text-foreground-950">本页目录</h2>
              <ul className="mt-3 flex flex-col gap-1">
                {sections.map((section) => (
                  <li key={section.id}>
                    <a
                      href={`#${section.id}`}
                      className="flex items-center gap-2 rounded-md px-2.5 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700"
                    >
                      <i className={`${section.icon} text-base`}></i>
                      {section.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="mt-5 rounded-lg border border-secondary-200 bg-secondary-100 p-4">
              <h2 className="flex items-center gap-2 font-heading text-sm font-semibold text-secondary-900">
                <i className="ri-information-line text-base"></i>
                阅读提示
              </h2>
              <p className="mt-2 text-xs leading-relaxed text-secondary-900/80">
                时间线内容整理自公开信息，若当事方有新的说明，我们会在原时间线末尾追加节点并更新状态。
              </p>
            </div>

            <div className="mt-5">
              <h2 className="mb-3 font-heading text-sm font-semibold text-foreground-950">
                相关事件
              </h2>
              <div className="flex flex-col gap-3">
                {related.map((item) => (
                  <Link
                    key={item.id}
                    to={`/event/${item.slug}`}
                    className="group flex gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
                  >
                    <span className="h-16 w-20 shrink-0 overflow-hidden rounded-md border border-background-200">
                      <img
                        src={item.cover}
                        alt={`${item.title}封面图`}
                        title={`${item.title} 热搜吃瓜相关事件`}
                        className="h-full w-full object-cover object-top"
                      />
                    </span>
                    <span className="min-w-0">
                      <span className="line-clamp-2 text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-700">
                        {item.title}
                      </span>
                      <span className="mt-1 block text-xs text-foreground-500">
                        {getCategoryName(item.categorySlug)} · {item.status}
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>

        <section className="w-full bg-background-100">
          <div className="mx-auto max-w-content px-4 py-12 md:px-6 md:py-16">
            <h2 className="mb-6 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              {moreTitle}
            </h2>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {moreEvents.map((item) => (
                <EventCard key={item.id} event={item} />
              ))}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}